import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BackendService } from './backend.service';

export interface AiRoleUploadUrlResponse {
  upload_url: string;
  s3_key: string;
}

const MAX_FILE_SIZE_BYTES = 150 * 1024; // 150 KB
const ALLOWED_EXTENSIONS = new Set(['pdf', 'docx', 'txt']);

@Injectable({ providedIn: 'root' })
export class AiRoleAgentService {
  constructor(
    private readonly http: HttpClient,
    private readonly backend: BackendService,
  ) {}

  /**
   * Request a presigned S3 PUT URL for a job description file upload.
   * POST /agents/AI_ROLE_TRANSFORM/upload
   */
  getUploadUrl(agentId: string, sessionId: string, filename: string, size?: number): Observable<AiRoleUploadUrlResponse> {
    const url = this.backend.createAgentsUrl(`agents/${agentId}/upload`);
    // `size` lets the endpoint enforce (and log) the per-file limit.
    const body: Record<string, unknown> = { session_id: sessionId, filename };
    if (typeof size === 'number') body['size'] = size;
    return this.http.post<any>(url, body).pipe(
      map(res => ({
        upload_url: res?.body?.upload_url as string,
        s3_key:     res?.body?.s3_key     as string,
      })),
    );
  }

  /**
   * Upload a file directly to S3 via the presigned PUT URL.
   * Uses native fetch() — NOT HttpClient — so the JWT Authorization
   * interceptor does not add an Authorization header that would break
   * the S3 presigned URL signature validation.
   */
  uploadFileToS3(presignedUrl: string, file: File): Observable<void> {
    const promise = fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: { 'Content-Type': 'application/octet-stream' },
    }).then(res => {
      if (!res.ok) throw new Error(`S3 upload failed: ${res.status} ${res.statusText}`);
    });
    return from(promise);
  }

  validateFile(file: File): string | null {
    const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ALLOWED_EXTENSIONS.has(ext)) {
      return `File type .${ext} is not supported. Please upload a PDF, Word document, or text file.`;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      return `File is too large (${(file.size / 1024).toFixed(0)} KB). Maximum size is 150 KB.`;
    }
    return null;
  }
}
